import { Zap } from "lucide-react";

/**
 * BroadcastABFields — subject-line A/B testing controls.
 *
 * Sends two subject-line variants to a small random slice (`test_percent`)
 * of the audience, waits `test_duration_minutes` for open-pixel signals,
 * then blasts the winning subject to everyone else. The heading, body, and
 * CTA come from the main compose form — only the subject differs between
 * variants.
 */
export default function BroadcastABFields({
  subjectA,
  subjectB,
  onSubjectAChange,
  onSubjectBChange,
  testPercent,
  onTestPercentChange,
  testDurationMinutes,
  onTestDurationChange,
  audienceCount,
}) {
  const each = audienceCount ? Math.max(1, Math.floor((audienceCount * testPercent) / 100)) : null;
  const reserved = audienceCount ? Math.max(0, audienceCount - each * 2) : null;

  return (
    <div className="space-y-3 border border-rose-500/30 bg-rose-950/10 rounded-md p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-300">
        <Zap size={14} /> A/B subject-line test
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
          Variant A subject
        </label>
        <input
          type="text"
          value={subjectA}
          onChange={(e) => onSubjectAChange(e.target.value)}
          maxLength={140}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
          placeholder="e.g. 🎁 Something new dropped inside"
          data-testid="broadcast-ab-subject-a"
        />
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
          Variant B subject
        </label>
        <input
          type="text"
          value={subjectB}
          onChange={(e) => onSubjectBChange(e.target.value)}
          maxLength={140}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
          placeholder="e.g. You just unlocked something…"
          data-testid="broadcast-ab-subject-b"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
            Split % (per variant)
          </label>
          <input
            type="number"
            min="1"
            max="25"
            value={testPercent}
            onChange={(e) => onTestPercentChange(parseInt(e.target.value || "5", 10))}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
            data-testid="broadcast-ab-percent"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
            Test window (minutes)
          </label>
          <input
            type="number"
            min="1"
            max="1440"
            value={testDurationMinutes}
            onChange={(e) => onTestDurationChange(parseInt(e.target.value || "30", 10))}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-rose-500 outline-none"
            data-testid="broadcast-ab-duration"
          />
        </div>
      </div>

      {audienceCount != null && each != null && (
        <div className="text-[11px] text-zinc-400 leading-relaxed">
          <span className="text-white">A</span> and <span className="text-white">B</span> each go to{" "}
          <span className="text-rose-300 font-semibold">{each.toLocaleString()}</span> members. The
          winning subject then blasts to the remaining{" "}
          <span className="text-rose-300 font-semibold">{reserved.toLocaleString()}</span> after the
          test window.
        </div>
      )}
    </div>
  );
}
